import styled from 'styled-components'
import { Model } from '../../../../types/model'
import {
  Text,
  PrimaryAction,
  gaps,
  paddings,
  SupportingText,
  AlertError,
  InputTextArea,
  TertiaryAction,
  colors,
  H6,
  SecondaryAction,
} from '@ohgoodlord/design-system'
import { FormEvent, useState } from 'react'
import { OverlayComponentProps } from '../../types'
import { FileUploadCard } from '../../../file-upload-card'
import { IFileOption } from '../../../../../components/file/utils'
import { FileSummary } from '../multi-file-upload-overlay/file-summary'
import { DefaultFooter } from '../default-footer'
import { genericDocumentUploadError, uploadFiles } from '../../../../utils'
import { logErrorToDatadog, track } from '../../../../../common/utils'
import { DefaultHeader } from '../default-header'
import {
  IdentityDocumentType,
  documentTitles,
  doesDocumentRequireBackPage,
} from './utils'

export const PROOF_OF_IDENTITY = 'proof-of-identity-overlay' as const

interface DocumentType {
  label: IdentityDocumentType
  icons: {
    checked: string
    unchecked: string
  }
  files: { label: string; placeholder: string }[]
}

export interface IdentityDocumentOverlayModel extends Model<typeof PROOF_OF_IDENTITY> {
  overlayTitle: string
  fileOptions: IFileOption[]
  hasAdditionalContext?: boolean
  fileDescription: string
  types: DocumentType[]
  preselectedDocumentType?: IdentityDocumentType
}

const StyledContainer = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: ${gaps.m};
  padding-bottom: ${paddings.l};
  overflow-y: scroll;
`

const StyledDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${gaps.s};
  justify-content: center;
  align-items: flex-start;
  padding: 0 20px;
`

const DocumentWrapper = styled.div<{ $selected: boolean }>`
  border: 1px solid ${colors.greyLight};
  border-radius: 4px;
  display: flex;
  flex-direction: row;
  cursor: pointer;
  gap: 12px;
  padding: 24px;
  align-items: center;
  justify-content: flex-start;
  margin: 0 12px;
  background-color: ${({ $selected }) => ($selected ? colors.buttonPrimary : 'inherit')};
  transition: background-color 0.2s ease-in-out;
`

const FileSummaryWrapper = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 150px;
  display: flex;
  flex-direction: column;
  row-gap: ${gaps.s};
  margin-bottom: 200px;
  overflow-y: hidden;
`

const StyledFooter = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  gap: 4px;
`

const StyledAdditionalDetailsWrapper = styled.div`
  position: relative;
  padding: 0 20px;
  .input-control-group .control-area textarea {
    min-height: 150px;
  }
`

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  position: absolute;
  top: -12%;
  left: 5%;
  background-color: white;
  z-index: 9999;
  transition: all 0.5s;
`

const Wrapper = styled.div`
  height: 100%;
  overflow-y: hidden;
`

const FileTipsWrapper = styled.ul`
  display: flex;
  gap: 12px;
  flex-direction: column;
  padding-left: 34px;
  margin: 20px 0;
`

export type Props = OverlayComponentProps<IdentityDocumentOverlayModel>

export const IdentityUploadOverlay = ({
  model,
  container,
  sas,
  uri,
  renderOverlayHeader,
  onOverlaySubmit,
  onSendEvent,
  onSendMessage,
  isMobile,
  onClose,
}: Props) => {
  const [frontPageFile, setFrontPageFile] = useState<File | undefined>(undefined)
  const [backPageFile, setBackPageFile] = useState<File | undefined>(undefined)

  const [selectedFileType, setSelectedFileType] = useState<
    IdentityDocumentType | undefined
  >(model.preselectedDocumentType)
  const [fileUploadError, setFileUploadError] = useState<string | null>(null)
  const [additionalDetails, setAdditionalDetails] = useState<string>('')
  const [additionalContextExpanded, setAdditionalContextExpanded] =
    useState<boolean>(false)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [frontPageFileUrl, setFrontPageFileUrl] = useState<string | undefined>(undefined)

  const [backPageFileUrl, setBackPageFileUrl] = useState<string | undefined>(undefined)

  const hasUploadedFiles =
    frontPageFile &&
    selectedFileType &&
    (!doesDocumentRequireBackPage(selectedFileType) || backPageFile)

  const fileDislayName = selectedFileType?.toLocaleLowerCase()

  return (
    <>
      {selectedFileType !== undefined && selectedFileType !== null ? (
        <Wrapper>
          <StyledContainer>
            <DefaultHeader
              modal={model.modal}
              isMobile={isMobile}
              title={documentTitles[selectedFileType] ?? 'Upload identity document'}
              onClose={onClose}
            />
            <StyledDescription>
              {selectedFileType === IdentityDocumentType.Selfie ? (
                <FileTipsWrapper>
                  <li>
                    <SupportingText>
                      Take a selfie showing your head and shoulders.
                    </SupportingText>
                  </li>
                  <li>
                    <SupportingText>
                      Ensure your face is well lit on a plain background.
                    </SupportingText>
                  </li>
                  <li>
                    <SupportingText>Your document is a PNG, JPEG, or PDF</SupportingText>
                  </li>
                </FileTipsWrapper>
              ) : (
                <div>
                  <Text align="left">Make sure:</Text>
                  <FileTipsWrapper>
                    <li>
                      <SupportingText>The whole page is visible</SupportingText>
                    </li>
                    <li>
                      <SupportingText>Your face is nice and clear</SupportingText>
                    </li>
                    <li>
                      <SupportingText>
                        Your document is a PNG, JPEG, or PDF
                      </SupportingText>
                    </li>
                  </FileTipsWrapper>
                </div>
              )}

              <FileUploadCard
                setFiles={files => {
                  const reader = new FileReader()

                  reader.onload = function (event) {
                    // The reader can return an ArrayBuffer type, but as we're reading from a file it should always return a string
                    const result = event?.target?.result as string
                    setFrontPageFileUrl(result)
                  }

                  reader.readAsDataURL(files[0])

                  track(`Click - Selected ${selectedFileType} front page`, {})
                  setFrontPageFile(files[0])
                }}
                allowMultipleFiles={false}
                withFilePreview
                fileInputId="front-page-file-input"
                currentFileUrl={frontPageFileUrl}
                isMobile={isMobile}
                currentFileIsPdf={frontPageFile?.type === 'application/pdf'}
                fileName={selectedFileType}
                uploadText={
                  doesDocumentRequireBackPage(selectedFileType)
                    ? `Upload the front of your ${fileDislayName}`
                    : undefined
                }
              />

              {doesDocumentRequireBackPage(selectedFileType) && (
                <FileUploadCard
                  setFiles={files => {
                    const reader = new FileReader()

                    reader.onload = function (event) {
                      // The reader can return an ArrayBuffer type, but as we're reading from a file it should always return a string
                      const result = event?.target?.result as string
                      setBackPageFileUrl(result)
                    }

                    reader.readAsDataURL(files[0])

                    track(`Click - Selected ${selectedFileType} back page`, {})
                    setBackPageFile(files[0])
                  }}
                  allowMultipleFiles={false}
                  withFilePreview
                  fileInputId="back-page-file-input"
                  currentFileUrl={backPageFileUrl}
                  isMobile={isMobile}
                  currentFileIsPdf={backPageFile?.type === 'application/pdf'}
                  fileName={selectedFileType}
                  uploadText={
                    doesDocumentRequireBackPage(selectedFileType)
                      ? `Upload the back of your ${fileDislayName}`
                      : undefined
                  }
                />
              )}
            </StyledDescription>
            {model.hasAdditionalContext && (
              <>
                <StyledAdditionalDetailsWrapper>
                  <TitleWrapper onClick={() => setAdditionalContextExpanded(true)}>
                    <>
                      {additionalContextExpanded ? (
                        <SupportingText>Supporting details (optional)</SupportingText>
                      ) : (
                        <TertiaryAction>Add supporting details (optional)</TertiaryAction>
                      )}
                    </>
                  </TitleWrapper>
                  {additionalContextExpanded && (
                    <InputTextArea
                      value={additionalDetails}
                      onChange={(e: FormEvent<HTMLTextAreaElement>) => {
                        setAdditionalDetails(e.currentTarget.value)
                      }}
                      name="additionalDetails"
                      id="additionalDetails"
                    ></InputTextArea>
                  )}
                </StyledAdditionalDetailsWrapper>
              </>
            )}
            <FileSummaryWrapper>
              <FileSummary
                handleRemoveFile={fileIndexToRemove => {
                  track('Click - Remove selected file', {})
                  const isRemovingBackPage = fileIndexToRemove > 0

                  if (isRemovingBackPage) {
                    setBackPageFile(undefined)
                    setBackPageFileUrl(undefined)
                    const backPageInput = document?.getElementById(
                      'back-page-file-input',
                    ) as HTMLInputElement
                    backPageInput.value = ''
                    return
                  }

                  const frontPageInput = document?.getElementById(
                    'front-page-file-input',
                  ) as HTMLInputElement

                  setFrontPageFile(undefined)
                  setFrontPageFileUrl(undefined)
                  frontPageInput.value = ''
                }}
                files={[
                  ...(frontPageFile ? [frontPageFile] : []),
                  ...(backPageFile ? [backPageFile] : []),
                ]}
              />
            </FileSummaryWrapper>
          </StyledContainer>
          <DefaultFooter
            renderActions={() => (
              <>
                <StyledFooter>
                  {fileUploadError && (
                    <AlertError>
                      <Text>{fileUploadError}</Text>
                    </AlertError>
                  )}

                  {model.preselectedDocumentType !== IdentityDocumentType.Selfie &&
                    model.preselectedDocumentType !== IdentityDocumentType.Passport && (
                      <SecondaryAction
                        onClick={() => {
                          setSelectedFileType(undefined)
                          setFrontPageFile(undefined)
                          setBackPageFile(undefined)
                          setFrontPageFileUrl(undefined)
                          setBackPageFileUrl(undefined)
                        }}
                      >
                        Back
                      </SecondaryAction>
                    )}

                  <PrimaryAction
                    testId="modal-primary-action-button"
                    disabled={!hasUploadedFiles}
                    loading={isUploading}
                    isFullWidth
                    onClick={async () => {
                      setIsUploading(true)
                      track('Click - Uploading files', {})
                      uploadFiles(
                        [
                          ...(frontPageFile ? [frontPageFile] : []),
                          ...(backPageFile ? [backPageFile] : []),
                        ],
                        container,
                        sas,
                        uri,
                        'proof-of-identity',
                      )
                        .then(fileData => {
                          onSendEvent({
                            name: 'form',
                            value: {
                              ...fileData,
                              documentType: selectedFileType,
                            },
                          })
                          onSendMessage({
                            message: `Attached ${fileDislayName}`,
                          })
                          onOverlaySubmit()
                        })
                        .catch(e => {
                          logErrorToDatadog('Identity overlay upload error', {
                            message: e.message ?? '',
                            error: e,
                          })
                          setFileUploadError(
                            e.message && e.message !== ''
                              ? e.message
                              : genericDocumentUploadError,
                          )
                        })
                        .finally(() => {
                          setIsUploading(false)
                        })
                    }}
                  >
                    Done
                  </PrimaryAction>
                </StyledFooter>
              </>
            )}
          />
        </Wrapper>
      ) : (
        <Wrapper>
          <StyledContainer>
            {renderOverlayHeader !== undefined && renderOverlayHeader()}

            <StyledDescription>
              {model.fileDescription && (
                <Text align="left">Choose a document to use to verify your identity</Text>
              )}
            </StyledDescription>

            <div id="identity-options">
              {model.types.map(document => (
                <DocumentWrapper
                  $selected={selectedFileType === document.label}
                  onClick={() => {
                    setSelectedFileType(document.label)
                  }}
                  key={document.label}
                >
                  <img width={36} src={document.icons?.checked} />
                  <H6>{document.label}</H6>
                </DocumentWrapper>
              ))}
            </div>
          </StyledContainer>
        </Wrapper>
      )}
    </>
  )
}
